import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { api } from './api';
import { currentSchool } from './school';

export interface PublicSchool {
  name: string;
  slug: string;
  suspended: boolean;
  brandColor: string | null;
  logoUrl: string | null;
  signInImageUrl: string | null;
  /** Empty when the school removed it. */
  signInHeadline: string;
  signInMessage: string;
  signIn: { google: boolean; password: boolean };
  /** Whether the school has public knowledgebase articles to link to. */
  knowledgebase: boolean;
}

/** The current school's name, logo, color, and sign-in options. Public, so it works before signing in. */
export const usePublicSchool = () =>
  useQuery({
    queryKey: ['public-school', currentSchool],
    queryFn: () => api.get<PublicSchool>(`/public/schools/${currentSchool}`),
    enabled: !!currentSchool,
    staleTime: 5 * 60_000,
    retry: false,
  });

const SHADES = ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'] as const;

/** A Tailwind-style scale from one color: lighter shades mix toward white, darker ones toward black. */
export function brandPalette(hex: string): Record<(typeof SHADES)[number], string> {
  const mix = (percent: number, toward: 'white' | 'black') => `color-mix(in oklab, ${hex} ${percent}%, ${toward})`;
  return {
    '50': mix(8, 'white'),
    '100': mix(16, 'white'),
    '200': mix(30, 'white'),
    '300': mix(48, 'white'),
    '400': mix(72, 'white'),
    '500': hex,
    '600': mix(86, 'black'),
    '700': mix(72, 'black'),
    '800': mix(60, 'black'),
    '900': mix(48, 'black'),
    '950': mix(32, 'black'),
  };
}

export function applyBrandColor(hex: string | null | undefined) {
  const style = document.documentElement.style;
  for (const shade of SHADES) style.removeProperty(`--color-brand-${shade}`);
  if (!hex || !/^#[0-9a-f]{6}$/i.test(hex)) return;
  for (const [shade, value] of Object.entries(brandPalette(hex))) style.setProperty(`--color-brand-${shade}`, value);
}

/** Contrast of white text on the color (WCAG). Buttons use darker shades, but very light colors still wash out. */
export function contrastWithWhite(hex: string) {
  const channel = (index: number) => {
    const value = parseInt(hex.slice(1 + index * 2, 3 + index * 2), 16) / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };
  const luminance = 0.2126 * channel(0) + 0.7152 * channel(1) + 0.0722 * channel(2);
  return 1.05 / (luminance + 0.05);
}

/** Paints the app in the school's color while it's mounted. */
export function SchoolBranding() {
  const school = usePublicSchool();
  const color = school.data?.brandColor;
  useEffect(() => {
    applyBrandColor(color);
    return () => applyBrandColor(null);
  }, [color]);
  return null;
}
